import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, RotateCcw, Clock, CheckCircle2, XCircle, AlertTriangle, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { buildErrorToast } from '@/lib/apiError';
import { useClients } from '@/hooks/useClient';
import {
  getReportRuns,
  resendReportRun,
  type IReportRunFilters,
} from '@/api/daily-report.api';

const STATUS_CONFIG: Record<string, { color: string; icon: typeof CheckCircle2 }> = {
  Pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  Generated: { color: 'bg-blue-100 text-blue-800', icon: FileText },
  Sent: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  Failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
};

export function ReportRunsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [resendConfirmId, setResendConfirmId] = useState<string | null>(null);

  // BA §S50: Pre-fill clientId from query param (linked from S49)
  const initialClientId = searchParams.get('clientId') || undefined;

  const [filters, setFilters] = useState<IReportRunFilters>({
    page: 1,
    pageSize: 20,
    clientId: initialClientId,
  });
  const [showFilters, setShowFilters] = useState(!!initialClientId);

  // BA §S50: Client multi-select filter
  const { data: clientsData } = useClients({ limit: 200 });
  const clients = clientsData?.data || [];

  const { data, isLoading } = useQuery({
    queryKey: ['report-runs', filters],
    queryFn: () => getReportRuns(filters),
  });

  const resendMutation = useMutation({
    mutationFn: resendReportRun,
    onSuccess: () => {
      toast({ title: 'Resend queued' });
      setResendConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['report-runs'] });
    },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to resend'),
  });

  const runs = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / (filters.pageSize || 20));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email History – Daily Report</h1>
          <p className="text-sm text-muted-foreground">S50 — Report run history and resend management</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-1"
        >
          <Filter className="h-4 w-4" />
          Filters
        </Button>
      </div>

      {/* Filters — BA §S50: includes Client multi-select */}
      {showFilters && (
        <div className="rounded-lg border p-4 grid grid-cols-5 gap-3">
          {/* BA §S50: Client filter */}
          <div className="space-y-1">
            <Label>Client</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={filters.clientId || ''}
              onChange={(e) => setFilters({ ...filters, clientId: e.target.value || undefined, page: 1 })}
            >
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.code ? ` (${c.code})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Date From</Label>
            <Input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value || undefined, page: 1 })}
            />
          </div>
          <div className="space-y-1">
            <Label>Date To</Label>
            <Input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value || undefined, page: 1 })}
            />
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined, page: 1 })}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Generated">Generated</option>
              <option value="Sent">Sent</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>Test Mode</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={filters.isTestMode === undefined ? '' : String(filters.isTestMode)}
              onChange={(e) => setFilters({ ...filters, isTestMode: e.target.value === '' ? undefined : e.target.value === 'true', page: 1 })}
            >
              <option value="">All</option>
              <option value="true">Test Only</option>
              <option value="false">Production Only</option>
            </select>
          </div>
        </div>
      )}

      {/* Table — BA §S50: includes Scheduled, Recipients Original, Error columns */}
      {isLoading ? (
        <p className="text-muted-foreground py-4">Loading report runs...</p>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="border-b text-left text-muted-foreground">
                <th className="p-3">Date</th>
                <th className="p-3">Client</th>
                <th className="p-3">Status</th>
                <th className="p-3">Staff</th>
                <th className="p-3">Leave</th>
                <th className="p-3">Absent</th>
                <th className="p-3">Scheduled</th>
                <th className="p-3">Recipients</th>
                <th className="p-3">Sent At</th>
                <th className="p-3">PDF</th>
                <th className="p-3">Error</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={12} className="p-6 text-center text-muted-foreground italic">
                    No report runs found.
                  </td>
                </tr>
              ) : (
                runs.map((run) => {
                  const statusConf = STATUS_CONFIG[run.status] || STATUS_CONFIG.Pending;
                  const StatusIcon = statusConf.icon;
                  return (
                    <tr key={run.id} className="border-b last:border-0 hover:bg-accent/30">
                      <td className="p-3 font-medium">{run.reportDate.split('T')[0]}</td>
                      <td className="p-3">{run.client?.name || '—'}</td>
                      <td className="p-3">
                        <Badge className={`${statusConf.color} gap-1`}>
                          <StatusIcon className="h-3 w-3" />
                          {run.status}
                          {run.isTestMode && <span className="ml-1 opacity-70">(Test)</span>}
                        </Badge>
                      </td>
                      <td className="p-3">{run.staffCount ?? '—'}</td>
                      <td className="p-3">{run.leaveCount ?? '—'}</td>
                      <td className="p-3">{run.absentCount ?? '—'}</td>
                      {/* BA §S50: Scheduled column */}
                      <td className="p-3 text-muted-foreground text-xs">
                        {run.scheduledSendAt ? new Date(run.scheduledSendAt).toLocaleString() : '—'}
                      </td>
                      {/* BA §S50: Recipients (Actual + Original tooltip) */}
                      <td className="p-3 text-xs text-muted-foreground max-w-[160px] truncate">
                        {run.recipientsActual || run.recipientsOriginal ? (
                          <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                              <span className="cursor-help underline decoration-dotted">
                                {(run.recipientsActual || '—').split(';').join(', ')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="space-y-1">
                                <p className="font-medium">Actual: {run.recipientsActual?.split(';').join(', ') || '—'}</p>
                                {run.recipientsOriginal && (
                                  <p className="text-muted-foreground">Original: {run.recipientsOriginal.split(';').join(', ')}</p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : '—'}
                      </td>
                      <td className="p-3 text-muted-foreground text-xs">
                        {run.actualSentAt ? new Date(run.actualSentAt).toLocaleString() : '—'}
                      </td>
                      <td className="p-3">
                        {run.pdfUrl ? (
                          <a
                            href={run.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                          >
                            <Download className="h-3 w-3" />
                            PDF
                          </a>
                        ) : '—'}
                      </td>
                      {/* BA §S50: Error column with tooltip */}
                      <td className="p-3">
                        {run.errorMessage ? (
                          <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 text-destructive text-xs cursor-help">
                                <AlertTriangle className="h-3 w-3" />
                                Error
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm">
                              {run.errorMessage}
                            </TooltipContent>
                          </Tooltip>
                        ) : '—'}
                      </td>
                      <td className="p-3">
                        {/* BA §S50: Confirm dialog before resend */}
                        {run.status === 'Failed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setResendConfirmId(run.id)}
                            disabled={resendMutation.isPending}
                            className="h-7 text-xs gap-1"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Resend
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {((filters.page || 1) - 1) * (filters.pageSize || 20) + 1}–
            {Math.min((filters.page || 1) * (filters.pageSize || 20), total)} of {total}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={(filters.page || 1) <= 1}
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(filters.page || 1) >= totalPages}
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* BA §S50: Confirm dialog before resend */}
      <Dialog open={!!resendConfirmId} onOpenChange={(open) => { if (!open) setResendConfirmId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Resend</DialogTitle>
            <DialogDescription>
              Are you sure you want to resend this daily report? The email will be queued for immediate delivery.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendConfirmId(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => { if (resendConfirmId) resendMutation.mutate(resendConfirmId); }}
              disabled={resendMutation.isPending}
            >
              {resendMutation.isPending ? 'Sending...' : 'Yes, Resend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
